import type { StudyInvestigativeQuestionSelectDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { useGetMeiaStudyInvestigativeQuestions } from '@queries/meia.query';
import { EditIcon, FileQuestionIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MeiaInvestigativeQuestionModal } from './meia-investigative-question-modal.component';

type MeiaInvestigativeQuestionsTileProps = {
  canEdit: boolean;
  studyId: string;
};

const QuestionCard: FC<{
  canEdit: boolean;
  onEdit: () => void;
  question: StudyInvestigativeQuestionSelectDto;
}> = (props) => {
  const { canEdit, onEdit, question } = props;

  return (
    <div className="flex h-fit flex-col gap-2 rounded-md bg-gray-100 p-2">
      <Tile
        className="rounded-md p-2 shadow-none"
        contentClassName="gap-2"
        header={{
          iconButton: canEdit
            ? {
                icon: <EditIcon size={14} />,
                onClick: onEdit,
              }
            : undefined,
          title: (
            <div className="flex items-start gap-2">
              <FileQuestionIcon className="mt-0.5 size-4 shrink-0 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">{question.questionText}</span>
            </div>
          ),
        }}
        hoverReveal={canEdit}
      >
        {question.answer ? (
          <p className="line-clamp-3 pl-6 text-xs text-gray-500">{question.answer}</p>
        ) : (
          <p className="pl-6 text-xs italic text-gray-400">No answer recorded.</p>
        )}
      </Tile>
    </div>
  );
};

export const MeiaInvestigativeQuestionsTile: FC<MeiaInvestigativeQuestionsTileProps> = (props) => {
  const { canEdit, studyId } = props;
  const { data: questions = [], isLoading } = useGetMeiaStudyInvestigativeQuestions({ studyId });
  const [editingQuestionId, setEditingQuestionId] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);

  const editingQuestion = questions.find((question) => question.id === editingQuestionId);
  const hasQuestions = questions.length > 0;
  const canAdd = canEdit;

  const headerButton = canAdd ? { onClick: () => setIsCreating(true), text: '+ Add' } : undefined;

  const emptyState = canAdd
    ? { message: 'Add Question', onClick: () => setIsCreating(true), title: 'No Investigative Questions' }
    : {
        title: 'No Investigative Questions',
      };

  return (
    <>
      <Tile
        emptyState={emptyState}
        header={{
          button: headerButton,
          title: 'Investigative Questions',
        }}
        isEmpty={!hasQuestions}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-3">
          {questions.map((question) => (
            <QuestionCard
              canEdit={canEdit}
              key={question.id}
              onEdit={() => setEditingQuestionId(question.id)}
              question={question}
            />
          ))}
        </div>
      </Tile>

      {canEdit && isCreating && (
        <MeiaInvestigativeQuestionModal
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          studyId={studyId}
        />
      )}
      {canEdit && editingQuestion && (
        <MeiaInvestigativeQuestionModal
          isOpen={Boolean(editingQuestionId)}
          onClose={() => setEditingQuestionId(undefined)}
          question={editingQuestion}
          studyId={studyId}
        />
      )}
    </>
  );
};
